import React from 'react';
import { TimePicker } from '@mui/x-date-pickers';
import { SxProps, ThemeProvider, useTheme } from '@mui/material';
import { CacheProvider } from '@emotion/react';
import moment from 'moment';
import { useAuth } from '../../session/InternalAuthProvider';
import { AccountData, HolidayCalendar } from '../../api';
import { getTimeFormat } from '../getDateAndTimeFormat';
import { cacheLtr } from '../GeneralUtils';

interface SharedMobileTimePickerProps {
  // Core required props
  value: moment.Moment | number; // Support both moment and unix timestamp
  onChange: (newValue: moment.Moment | null) => void;
  label?: string | JSX.Element;

  // Time constraints
  minTime?: moment.Moment;
  maxTime?: moment.Moment;
  minutesStep?: number; // default: 15

  // Form integration
  name?: string;
  disabled?: boolean;
  format?: string; // For FormTimePicker timeFormat support

  // Styling & behavior
  sx?: SxProps;
  showUpdatedStyling?: boolean; // Green border for FormTimePicker
  onAccept?: (value: moment.Moment | null) => void;

  // Formik support
  field?: any; // For {...field} spreading
}

export const SharedMobileTimePicker = ({
  value,
  onChange,
  label,
  minTime,
  maxTime,
  minutesStep = 15,
  name,
  disabled,
  format,
  sx,
  showUpdatedStyling = false,
  onAccept,
  field,
}: SharedMobileTimePickerProps) => {
  const theme = useTheme();
  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData;

  const isRtl = theme.direction === 'rtl';

  // Auto-detect ampm format from account settings
  const ampm = account.holiday_calendar === HolidayCalendar.US;

  // Auto-detect time format if not provided
  const timeFormat = format || getTimeFormat(account);

  // Convert value to moment object
  const momentValue = typeof value === 'number' ? moment.unix(value).tz('utc') : value;

  // Style for FormTimePicker green border when field is updated
  const updatedStyling = showUpdatedStyling
    ? {
        '&& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
          borderColor: '#47b406',
          borderWidth: '4px',
        },
        '&& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderColor: '#47b406',
          borderWidth: '4px',
        },
      }
    : {};

  // Add LTR styling and fix Hebrew dropdown column order
  const timePickerStyling = {
    '& .MuiPickersPopper-root': {
      direction: 'ltr',
    },
    '& .MuiTimePickerToolbar-root': {
      direction: 'ltr',
    },
    '& .MuiTimeClock-root': {
      direction: 'ltr',
    },
    '& .MuiMultiSectionDigitalClock-root': {
      direction: 'ltr !important',
      display: 'flex',
      // Use row-reverse for Hebrew to fix column order, row for other languages
      flexDirection: isRtl ? 'row-reverse !important' : 'row !important',
    },
    // Force all MUI time picker containers to use LTR with appropriate flex direction
    '& [class*="MuiMultiSectionDigitalClock-root"]': {
      direction: 'ltr !important',
      flexDirection: isRtl ? 'row-reverse !important' : 'row !important',
    },
  };

  // Combine all sx props
  const combinedSx = {
    ...updatedStyling,
    ...timePickerStyling,
    ...sx,
  };

  // Base props that are always applied
  const baseProps = {
    value: momentValue,
    onChange,
    label,
    ampm,
    minutesStep,
    format: timeFormat,
    ...(minTime && { minTime }),
    ...(maxTime && { maxTime }),
    ...(name && { name }),
    ...(disabled !== undefined && { disabled }),
    ...(onAccept && { onAccept }),
    sx: combinedSx,
  };

  return (
    <CacheProvider value={cacheLtr}>
      <ThemeProvider theme={{ ...theme, direction: 'ltr' }}>
        <TimePicker
          {...(field || {})} // Spread Formik field props if provided
          {...baseProps}
        />
      </ThemeProvider>
    </CacheProvider>
  );
};

export default SharedMobileTimePicker;
